import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import QnaListItem from './QnaListItem';
import QnaListOrdering from './QnaListOrdering';
import { ExtendedQnaPair } from '../types';
import { exportQnaPairsToCSV } from '../utils/csvUtils';

interface QnaListProps {
  qnaList: ExtendedQnaPair[];
  filteredQnaList: ExtendedQnaPair[];
  deletingQnaIds: string[];
  editingQnaIds: string[];
  page: number;
  rowsPerPage: number;
  deletingAll: boolean;
  deleteAllDialogOpen: boolean;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onSave: (id: string) => void;
  onCancel: (id: string) => void;
  onUpdateField: (id: string, field: 'question' | 'answer', value: string) => void;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: SelectChangeEvent<number>) => void;
  onDeleteAll: (filteredPairs?: ExtendedQnaPair[]) => void;
  onTagClick: (tag: string) => void;
  onOpenDeleteAllDialog: () => void;
  onCloseDeleteAllDialog: () => void;
  onSemanticSearch: (query: string) => void; // Add this line
  sortOrder?: 'newest' | 'oldest'; // Add sortOrder prop
  onSortOrderChange?: (newOrder: 'newest' | 'oldest') => void; // Add callback for manual sort changes
}

const QnaList: React.FC<QnaListProps> = ({
  qnaList,
  filteredQnaList,
  deletingQnaIds,
  editingQnaIds,
  page,
  rowsPerPage,
  deletingAll,
  deleteAllDialogOpen,
  onEdit,
  onDelete,
  onSave,
  onCancel,
  onUpdateField,
  onChangePage,
  onChangeRowsPerPage,
  onDeleteAll,
  onTagClick,
  onOpenDeleteAllDialog,
  onCloseDeleteAllDialog,
  onSemanticSearch, // Add this line
  sortOrder = 'newest',
  onSortOrderChange
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedQnaId, setSelectedQnaId] = useState<string | null>(null);
  const [sourceFilter] = useState<string>('all');
  const [filteredBySourceList, setFilteredBySourceList] = useState<ExtendedQnaPair[]>(filteredQnaList);
  // Update ordering state type
  const [ordering, setOrdering] = useState<'newest' | 'oldest'>(sortOrder);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Sync local ordering state with passed sortOrder prop
  useEffect(() => {
    console.log('[QnaList] Syncing ordering state - sortOrder prop:', sortOrder, 'current ordering:', ordering);
    if (sortOrder !== ordering) {
      setOrdering(sortOrder);
      console.log('[QnaList] Updated local ordering to:', sortOrder);
    }
  }, [sortOrder, ordering]);

  useEffect(() => {
    if (sourceFilter === 'all') {
      setFilteredBySourceList(filteredQnaList);
    } else {
      const filtered = filteredQnaList.filter(qna => {
        if (!qna.tags || !Array.isArray(qna.tags)) return false;
        
        switch (sourceFilter) {
          case 'manual':
            return qna.tags.includes('Manual');
          case 'webpage':
            // Filter for URLs (tags that start with http)
            return qna.tags.some(tag => tag.startsWith('http'));
          case 'database':
            // Filter for database sources (tags that start with 'database-')
            return qna.tags.some(tag => tag.startsWith('database-'));
          case 'file':
            // Filter for file sources (tags that don't match the other patterns)
            return qna.tags.some(tag => 
              !tag.includes('Manual') && 
              !tag.startsWith('http') && 
              !tag.startsWith('database-')
            );
          default:
            return true;
        }
      });
      setFilteredBySourceList(filtered);
    }
  }, [sourceFilter, filteredQnaList]);

  // Create pre-merged list with editing info BEFORE ordering
  const preMergedList = filteredBySourceList.map(filteredItem => {
    const mainListItem = qnaList.find(qna => qna.qnaId === filteredItem.qnaId);
    if (mainListItem) {
      const isBeingEdited = editingQnaIds.includes(filteredItem.qnaId);
      return {
        ...filteredItem,
        // Include editing state if item is being edited
        ...(isBeingEdited && {
          editedQuestion: mainListItem.editedQuestion,
          editedAnswer: mainListItem.editedAnswer,
          isEditing: mainListItem.isEditing
        })
      };
    }
    return filteredItem;
  });

  // Ordering logic that operates on pre-merged list with highlighting
  const getOrderedList = () => {
    let list = [...preMergedList];
    if (ordering === 'newest') {
      return list.sort((a, b) => (b.createdAt && a.createdAt ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() : 0));
    } else if (ordering === 'oldest') {
      return list.sort((a, b) => (a.createdAt && b.createdAt ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime() : 0));
    } else if (ordering === 'test') {
      // Test ordering: just sort by newest (no highlighting system)
      return list.sort((a, b) => (b.createdAt && a.createdAt ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime() : 0));
    }
    return list;
  };

  const mergedList = getOrderedList();
  
  const paginatedQnaList = mergedList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );


  const handleOpenDeleteDialog = (qnaId: string) => {
    setSelectedQnaId(qnaId);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedQnaId(null);
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = () => {
    if (selectedQnaId) {
      onDelete(selectedQnaId);
    }
    handleCloseDeleteDialog();
  };

  const handleExportCSV = () => {
    const dataToExport = filteredBySourceList.length > 0 ? filteredBySourceList : mergedList;
    const filename = `qna-export-filtered-${new Date().toISOString().split('T')[0]}.csv`;
    exportQnaPairsToCSV(dataToExport, filename);
  };

  return (
    <Box mt={4} sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          All Q&A Pairs from All Sources ({mergedList.length} of {qnaList.length})
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={handleExportCSV}
            disabled={mergedList.length === 0}
          >
            Export {mergedList.length} Q&A pairs
          </Button>
          <QnaListOrdering
            ordering={ordering}
            onOrderingChange={(ord) => {
              setOrdering(ord);
              
              // Notify parent component of manual sort order change
              if (onSortOrderChange) {
                onSortOrderChange(ord);
              }
            }}
            onDeleteAll={onOpenDeleteAllDialog}
            disableDeleteAll={mergedList.length === 0 || deletingAll}
            isFiltered={filteredBySourceList.length !== qnaList.length}
            filteredCount={filteredBySourceList.length}
          />
        </Box>
      </Box>
      {mergedList.length > 0 ? (
        <>
          {paginatedQnaList.map((qna) => {
            const isTemp = qna.qnaId.toString().startsWith('temp-');
            // Ensure these are always boolean values by adding Boolean coercion
            const isDeleting = Boolean(deletingQnaIds.includes(qna.qnaId) || qna.isDeleting);
            const isEditing = Boolean(editingQnaIds.includes(qna.qnaId) || qna.isEditing);
            
            return (
              <QnaListItem
                key={qna.qnaId}
                qna={qna}
                isTemp={isTemp}
                isDeleting={isDeleting}
                isEditing={isEditing}
                onEdit={onEdit}
                onDelete={() => handleOpenDeleteDialog(qna.qnaId)}
                onSave={onSave}
                onCancel={onCancel}
                onUpdateField={onUpdateField}
                onTagClick={onTagClick}
              />
            );
          })}
          
          {/* Pagination controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Rows per page:
              </Typography>
              <Select
                value={rowsPerPage}
                onChange={onChangeRowsPerPage}
                size="small"
                sx={{ mr: 2 }}
              >
                {[5, 10, 25, 50].map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
              <Typography variant="body2">
                {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, mergedList.length)} of {mergedList.length}
              </Typography>
            </Box>
            
            <Box>
              <Button 
                onClick={() => onChangePage(null, page - 1)}
                disabled={page === 0}
                sx={{ minWidth: 'auto', mr: 1 }}
              >
                Previous
              </Button>
              <Button 
                onClick={() => onChangePage(null, page + 1)}
                disabled={page >= Math.ceil(mergedList.length / rowsPerPage) - 1}
                sx={{ minWidth: 'auto' }}
              >
                Next
              </Button>
            </Box>
          </Box>
          
          {/* Confirmation dialog for delete all */}
          <Dialog
            open={deleteAllDialogOpen}
            onClose={onCloseDeleteAllDialog}
            aria-labelledby="delete-all-dialog-title"
            aria-describedby="delete-all-dialog-description"
            fullScreen={isMobile}
          >
            <DialogTitle id="delete-all-dialog-title">
              Delete All Q&A Pairs
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-all-dialog-description">
                {filteredBySourceList.length !== qnaList.length ? (
                  <>
                    Are you sure you want to delete {filteredBySourceList.length} filtered Q&A pairs from this collection?
                    This will only delete the currently visible/filtered items, not all Q&A pairs.
                    This action cannot be undone.
                  </>
                ) : (
                  <>
                    Are you sure you want to delete all {filteredBySourceList.length} Q&A pairs from this collection?
                    This action cannot be undone.
                  </>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={onCloseDeleteAllDialog} disabled={deletingAll}>
                Cancel
              </Button>
              <Button 
                onClick={() => onDeleteAll(filteredBySourceList)} 
                color="error" 
                disabled={deletingAll}
                startIcon={deletingAll && <CircularProgress size={20} color="inherit" />}
              >
                {deletingAll ? 'Deleting...' : (filteredBySourceList.length !== qnaList.length ? `Delete ${filteredBySourceList.length} Filtered` : 'Delete All')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Confirmation dialog for individual delete */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleCloseDeleteDialog}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
            fullScreen={isMobile}
          >
            <DialogTitle id="delete-dialog-title">
              Delete Q&A Pair
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-dialog-description">
                Are you sure you want to delete this Q&A pair? This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDeleteDialog}>
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmDelete} 
                color="error"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No matching Q&A pairs found. Try adjusting your search criteria.
        </Typography>
      )}
    </Box>
  );
};

export default QnaList;